import { z } from 'zod';
import { baseQuantitySchema } from './operations';
const id = z.uuid(),
  evidence = z.string().min(1).max(4000),
  hash = z.string().regex(/^[a-f0-9]{64}$/),
  count = z.string().regex(/^[1-9]\d{0,17}$/),
  unit = z.enum(['g', 'ml']);
const batch = { batchId: id, evidence };
export const factoryPayloadSchema = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('authorize'),
      orderId: id,
      orderHash: hash,
      skuId: id,
      facility: z.string().min(1).max(200),
      plannedInputQuantity: baseQuantitySchema,
      unit,
      plannedUnits: count,
      validUntil: z.iso.datetime({ offset: true }),
      evidence,
    })
    .strict(),
  z.object({ action: z.literal('revoke_authorization'), authorizationId: id, evidence }).strict(),
  z
    .object({
      action: z.literal('start_batch'),
      authorizationId: id,
      label: z.string().min(1).max(200),
      evidence,
    })
    .strict(),
  z
    .object({
      action: z.literal('consume'),
      ...batch,
      reservationId: id,
      quantity: baseQuantitySchema,
      unit: z.enum(['g', 'ml', 'ea']),
    })
    .strict(),
  z
    .object({
      action: z.literal('output'),
      ...batch,
      quantity: baseQuantitySchema,
      lossQuantity: z.string().regex(/^(0|[1-9]\d{0,17})(\.\d{1,6})?$/),
      unit,
    })
    .strict(),
  z.object({ action: z.literal('deviation'), ...batch, description: evidence }).strict(),
  z
    .object({
      action: z.literal('decide_deviation'),
      deviationId: id,
      disposition: z.enum(['accepted', 'rejected']),
      evidence,
    })
    .strict(),
  z
    .object({
      action: z.literal('qa_evidence'),
      ...batch,
      planId: id,
      reportFileId: id,
      outcome: z.enum(['passed', 'failed']),
      measuredValue: evidence,
    })
    .strict(),
  z.object({ action: z.literal('release_batch'), ...batch }).strict(),
  z.object({ action: z.literal('pack'), ...batch, quantity: count }).strict(),
  z.object({ action: z.literal('release_pack'), packId: id, reportFileId: id, evidence }).strict(),
  z
    .object({
      action: z.literal('create_parcel'),
      packId: id.nullable(),
      sampleId: id.nullable(),
      sourceVersion: z.int().positive(),
      sourceHash: hash,
      orderId: id.nullable(),
      quantity: count,
      carrier: z.string().min(1).max(200),
      tracking: z.string().min(1).max(200),
      recipientPrivate: evidence,
      evidence,
    })
    .strict()
    .refine((v) => (v.packId === null) !== (v.sampleId === null), {
      message: 'exact_parcel_source_required',
    }),
  z
    .object({
      action: z.literal('parcel_event'),
      parcelId: id,
      event: z.enum([
        'dispatched',
        'in_transit',
        'carrier_delivered',
        'delivery_failed',
        'returned',
      ]),
      observedAt: z.iso.datetime({ offset: true }),
      evidence,
    })
    .strict(),
  z.object({ action: z.literal('share_parcel'), parcelId: id, evidence }).strict(),
]);
export const factoryRequestSchema = z
  .object({ organizationId: id, projectId: id, idempotencyKey: id, payload: factoryPayloadSchema })
  .strict();
export type FactoryCommand = z.infer<typeof factoryPayloadSchema>;
const parcel = z.object({
  id,
  sampleId: id.nullable(),
  orderId: id.nullable(),
  sourceVersion: z.int().positive(),
  sourceHash: hash,
  quantity: z.string(),
  carrier: z.string(),
  tracking: z.string(),
  events: z.array(z.object({ event: z.string(), observedAt: z.string() })),
});
export const deliverySummarySchema = z.array(parcel);
export type DeliverySummary = z.infer<typeof deliverySummarySchema>;
export const factoryWorkspaceSchema = z.object({
  authorizations: z.array(
    z.object({
      id,
      orderId: id,
      orderHash: hash,
      skuId: id,
      facility: z.string(),
      manifestHash: hash,
      plannedUnits: z.string(),
      plannedInputQuantity: z.string(),
      unit,
      validUntil: z.string(),
      revoked: z.boolean(),
      bom: z.array(
        z.object({
          specificationId: id,
          specificationHash: hash,
          quantity: z.string(),
          unit: z.enum(['g', 'ml', 'ea']),
        }),
      ),
    }),
  ),
  batches: z.array(
    z.object({
      id,
      authorizationId: id,
      label: z.string(),
      inputs: z.array(
        z.object({
          id,
          reservationId: id,
          specificationId: id,
          specificationHash: hash,
          specificationVersion: z.int().positive(),
          sourceSpecificationId: id,
          sourceSpecificationHash: hash,
          sourceSpecificationVersion: z.int().positive(),
          quantity: z.string(),
          unit: z.enum(['g', 'ml', 'ea']),
        }),
      ),
      output: z.object({ id, quantity: z.string(), lossQuantity: z.string(), unit }).nullable(),
      qaReleased: z.boolean(),
      deviations: z.array(z.object({ id, description: z.string(), disposition: z.string() })),
      packs: z.array(
        z.object({
          id,
          version: z.int(),
          hash,
          quantity: z.string(),
          finishedQuantity: z.string(),
          finishedUnit: unit,
          packagingId: id,
          artworkId: id,
          densityId: id.nullable(),
          qaReleased: z.boolean(),
        }),
      ),
    }),
  ),
  parcels: z.array(parcel.extend({ packId: id.nullable(), recipientPrivate: z.string() })),
});
export type FactoryWorkspace = z.infer<typeof factoryWorkspaceSchema>;
